import React, { useState, useEffect, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import DeleteIcon from '@mui/icons-material/Delete';
import { LoadingButton } from '@mui/lab';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { alpha } from '@mui/material/styles';

const ChangeImageDialog = ({ open, handleClose, isLoading, onSubmit }) => {
  const [previewImage, setPreviewImage] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!open) {
      setPreviewImage('');
      setSelectedImage(null);
      setIsDragging(false);
    }
  }, [open]);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    setIsDragging(false);
    
    if (rejectedFiles && rejectedFiles.length > 0) {
      toast.error('Please upload a valid image file (JPEG, PNG, WebP, GIF, or SVG)');
      return;
    }

    const imageFile = acceptedFiles[0];
    if (imageFile) {
      // Check file size (10MB limit)
      if (imageFile.size > 10 * 1024 * 1024) {
        toast.error('Image size should not exceed 10MB');
        return;
      }
      
      setSelectedImage(imageFile);
      setPreviewImage(URL.createObjectURL(imageFile));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'image/gif': ['.gif'],
      'image/svg+xml': ['.svg'],
    },
    onDrop,
    onDragEnter: () => setIsDragging(true),
    onDragLeave: () => setIsDragging(false),
    multiple: false,
    maxSize: 10 * 1024 * 1024, // 10MB
  });

  const handleRemove = () => {
    if (previewImage) {
      URL.revokeObjectURL(previewImage);
    }
    setPreviewImage('');
    setSelectedImage(null);
  };

  const handleSave = () => {
    if (!(selectedImage instanceof File)) {
      toast.error('Please select an image to upload');
      return;
    }

    const formData = new FormData();
    formData.append('image', selectedImage);
    onSubmit(formData);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="change-image-dialog-title"
      aria-describedby="change-image-dialog-description"
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ p: 0 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 3,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h4" sx={{ color: 'text.titles' }}>
            Change Profile Image
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          {/* Drag and Drop Area */}
          <Box
            {...getRootProps()}
            sx={{
              position: 'relative',
              border: '2px dashed',
              borderColor: isDragActive || isDragging ? 'primary.main' : 'divider',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              bgcolor: isDragActive || isDragging
                ? alpha('#A94B9C', 0.05)
                : 'background.paper',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: alpha('#A94B9C', 0.02),
              },
              minHeight: previewImage ? 'auto' : '300px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <input {...getInputProps()} />
            
            {previewImage ? (
              <Box sx={{ position: 'relative', width: '100%' }}>
                <Box
                  component="img"
                  src={previewImage}
                  alt="Preview"
                  sx={{
                    width: '100%',
                    maxWidth: '400px',
                    maxHeight: '400px',
                    objectFit: 'contain',
                    borderRadius: 2,
                    mb: 2,
                  }}
                />
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove();
                  }}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'error.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'error.dark',
                    },
                  }}
                >
                  <DeleteIcon />
                </IconButton>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                  Click or drag to replace image
                </Typography>
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: alpha('#A94B9C', 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  {isDragActive || isDragging ? (
                    <CloudUploadIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                  ) : (
                    <ImageIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                  )}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                  {isDragActive || isDragging ? 'Drop your image here' : 'Drag & drop your image'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  or click to browse
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Supports: JPEG, PNG, WebP, GIF, SVG (Max 10MB)
                </Typography>
              </>
            )}
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <LoadingButton
              fullWidth
              variant="outlined"
              onClick={handleClose}
              disabled={isLoading}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Cancel
            </LoadingButton>
            <LoadingButton
              loading={isLoading}
              fullWidth
              variant="contained"
              color="main"
              onClick={handleSave}
              disabled={!selectedImage}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Upload Image
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ChangeImageDialog;

